import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as walletService from '../../services/walletService'
import { getAffiliateMe, affiliateWithdrawRequest } from '../../services/affiliateService'
import type { AffiliateMeData } from '../../services/affiliateService'

const MIN_WITHDRAW = 100

const AffiliateWithdrawalPage: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [affiliateData, setAffiliateData] = useState<AffiliateMeData | null>(null)
  const [amount, setAmount] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const me = await getAffiliateMe()
        if (!cancelled) setAffiliateData(me)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const remaining = affiliateData?.remainingEarnings ?? 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const num = parseFloat(amount.replace(/,/g, ''))
    if (Number.isNaN(num) || num < MIN_WITHDRAW) {
      Swal.fire({
        icon: 'warning',
        title: 'จำนวนไม่ถูกต้อง',
        text: `กรุณาระบุจำนวนขั้นต่ำ ${MIN_WITHDRAW} บาท`,
      })
      return
    }
    if (num > remaining) {
      Swal.fire({
        icon: 'warning',
        title: 'ยอดไม่เพียงพอ',
        text: `รายได้คงเหลือ ${walletService.formatCurrency(remaining)}`,
      })
      return
    }
    setSubmitting(true)
    try {
      const res = await affiliateWithdrawRequest(num)
      if (res.success) {
        Swal.fire({
          icon: 'success',
          title: 'ส่งคำขอสำเร็จ',
          text: 'ระบบจะดำเนินการโอนเงินตามที่ระบุ',
        }).then(() => {
          setAmount('')
          navigate('overview')
        })
      } else {
        Swal.fire({
          icon: 'info',
          title: 'แจ้งเตือน',
          text: (res as { message?: string }).message || 'ไม่สามารถดำเนินการได้ในขณะนี้',
        })
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: msg,
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2 c_tertiary">กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="text-center py-4">
        <p className="c_primary mb-2">{error}</p>
        <Link to="/affiliate/overview" className="link_pmr btn border_30 border px-3 py-2 c_tertiary">
          กลับไปแนะนำเพื่อน
        </Link>
      </div>
    )
  }

  return (
    <div className="row mx__1">
      <div className="col-12 px-1 mb-2">
        <div className="border_5 bdc_primary bg_op_secondary py-2 overflow-hidden">
          <div className="c_tertiary text-center py-1">
            <h5 className="mb-0">รายได้คงเหลือ (บาท)</h5>
          </div>
          <div className="p-2">
            <h4 className="text-center c_primary mb-0">{walletService.formatCurrency(remaining)}</h4>
          </div>
        </div>
      </div>

      <div className="col-12 px-1 mb-2">
        <div className="border_5 bdc_primary bg_op_secondary p-3 overflow-hidden">
          <div className="c_tertiary mb-2">
            <h5 className="mb-0">
              <i className="fas fa-dollar-sign me-2" />
              ถอนคอมมิชชั่น
            </h5>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="mb-2">
              <label className="form-label c_tertiary">จำนวนเงิน (บาท)</label>
              <input
                type="text"
                className="form-control bg_secondary c_secondary border_5"
                placeholder={`ขั้นต่ำ ${MIN_WITHDRAW} บาท`}
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
                disabled={remaining < MIN_WITHDRAW}
              />
            </div>
            <button
              type="submit"
              className="link_pmr w-100 box_row justify-content-center font_rg bg_gd_quaternary border_30 border-0 py-2 c_tertiary"
              disabled={submitting || remaining < MIN_WITHDRAW}
            >
              {submitting ? 'กำลังส่งคำขอ...' : 'ส่งคำขอถอน'}
            </button>
          </form>
          {remaining < MIN_WITHDRAW && (
            <p className="c_quaternary small mt-2 mb-0">รายได้คงเหลือไม่ถึงขั้นต่ำในการถอน</p>
          )}
        </div>
      </div>

      <div className="col-12 px-1">
        <Link
          to="/affiliate/overview"
          className="link_pmr w-100 box_row justify-content-center font_rg bdc_primary bg_primary border_30 border py-2 text-decoration-none c_tertiary"
        >
          กลับไปแนะนำเพื่อน
        </Link>
      </div>
    </div>
  )
}

export default AffiliateWithdrawalPage
